import {
  resolveMobileBuilderSurface,
  type BuilderSurface,
  type MobileBuilderFlag,
} from '../lib/featureFlags';
import { useAuth } from './useAuth';
import { usePlatformSetting } from './usePlatformSetting';

const LS_KEY = 'vf:builder-surface';
const MOBILE_BREAKPOINT_PX = 768;

/**
 * Resolve a builder surface (mobile vs desktop) ativa pro user atual.
 *
 * Resolution order (prioridade crescente — primeiro match vence):
 *   1. URL override `?surface=mobile|desktop` (dev/QA only, undocumented)
 *   2. User preference em `localStorage['vf:builder-surface']`
 *   3. Breakpoint mobile (≤768px) → SEMPRE 'mobile' (mobile-ui.1.A, D-EPIC-1 Opção A)
 *   4. Platform setting `mobile_builder_enabled` (superadmin canary toggle — governa só >768px)
 *   5. Breakpoint fallback (max-width: 768px)
 *
 * Hooks (useAuth, usePlatformSetting) são chamados no topo — Rules of Hooks
 * impede early-returns ANTES de hook calls.
 */
export function usePreferredBuilderSurface(): BuilderSurface {
  const { user } = useAuth();
  const flag = usePlatformSetting<MobileBuilderFlag>('mobile_builder_enabled');

  // 1. URL override (dev/QA only, undocumented)
  if (typeof window !== 'undefined') {
    const urlOverride = new URLSearchParams(window.location.search).get('surface');
    if (urlOverride === 'mobile' || urlOverride === 'desktop') return urlOverride;
  }

  // 2. User preference (localStorage)
  if (typeof localStorage !== 'undefined') {
    const pref = localStorage.getItem(LS_KEY);
    if (pref === 'mobile' || pref === 'desktop') return pref;
  }

  // 3. Breakpoint mobile — viewport ≤768px SEMPRE resolve mobile; o DesktopBuilder
  //    é inutilizável nessa faixa. A flag passa a governar só >768px (mobile-ui.1.A).
  if (resolveBreakpoint() === 'mobile') return 'mobile';

  // 4. Platform setting
  const userForFlag = user ? { id: user.id, email: user.email ?? '' } : null;
  const resolution = resolveMobileBuilderSurface(flag, userForFlag);
  if (resolution === 'disabled') return 'desktop';

  // 5. Breakpoint fallback ('enabled' OU null caem aqui)
  return resolveBreakpoint();
}

function resolveBreakpoint(): BuilderSurface {
  if (typeof window === 'undefined') return 'desktop';
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches ? 'mobile' : 'desktop';
}
